import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  lastActivityAt,
  useSearchSessions,
  type SessionListItem,
} from "@/features/sessions";
import { useDebounce } from "@/features/utils/use-debounce";
import { useReadOnly } from "@/features/utils/api-scope";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { EmptyState } from "@/components/molecules/empty-state";
import { FilterMenuPill } from "@/components/molecules/filter-pills";
import { ErrorState } from "@/components/molecules/error-state";
import {
  ChatsCircleIcon,
  MagnifyingGlassIcon,
  XIcon,
} from "@phosphor-icons/react";
import { format, isThisYear, isToday, isYesterday } from "date-fns";
import { Fragment, useState } from "react";
import { CallButton } from "./call-button";
import { ConversationListItem } from "./conversation-list-item";

const PAGE_SIZE = 30;

/**
 * The left pane: search, filters, and the conversations themselves.
 *
 * It owns the query, not the page, so selecting a conversation never refetches
 * the list and the scroll position survives — the whole point of a two-pane
 * layout over the table it replaces, where reading a call meant leaving the
 * list and coming back to the top of it.
 */
export function ConversationList({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (session: SessionListItem) => void;
}) {
  const { t } = useTranslation("calls");
  const readOnly = useReadOnly();
  const [search, setSearch] = useState("");
  // Off, the list leaves out what nobody answered (`no_answer`: adverts and
  // hang-ups on the greeting, outbound legs that rang out): they are a large
  // share of a call log and there is nothing in them to read.
  const [category, setCategory] = useState<"no_answer" | "all" | undefined>();
  const [type, setType] = useState<
    "inbound" | "outbound" | "notifications" | undefined
  >();
  const [medium, setMedium] = useState<"call" | "chat" | "email" | undefined>();
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Typing filters the list, so the request waits for the typist to stop.
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, isFetching, isError, refetch } = useSearchSessions({
    category:
      category === "all" ? undefined : (category ?? "conversations"),
    type,
    medium,
    search: debouncedSearch || undefined,
    limit,
    // The last thing somebody said, not `updated_at`: a wrap-up rewrites the row
    // minutes after the call and used to bring it back to the top.
    sort_by: "last_message_at",
    sort_order: "desc",
  });

  const sessions = data?.data || [];
  const hasMore = data?.has_more || sessions.length < (data?.total || 0);
  const filtering = !!(category || type || medium || debouncedSearch);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-2 border-b p-3">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("list.searchPlaceholder")}
              className="pl-8 pr-8"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label={t("list.clearSearch")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          {/* Where a mail client puts "compose": at the head of the list it
              writes into, not in a page title bar this page no longer has. */}
          {!readOnly && <CallButton compact />}
        </div>

        {/* Three filters in 22rem: pills that open their choices, reading
            as the filter's name while off and as its value once set. */}
        <div className="flex flex-wrap gap-1.5">
          <FilterMenuPill
            label={t("filters.type")}
            clearLabel={t("filters.clear", { name: t("filters.type") })}
            value={type}
            onChange={setType}
            options={[
              { value: "inbound", label: t("types.inbound") },
              { value: "outbound", label: t("types.outbound") },
              { value: "notifications", label: t("types.notifications") },
            ]}
            allLabel={t("types.all")}
          />
          <FilterMenuPill
            label={t("filters.medium")}
            clearLabel={t("filters.clear", { name: t("filters.medium") })}
            value={medium}
            onChange={setMedium}
            options={[
              { value: "call", label: t("mediums.call") },
              { value: "chat", label: t("mediums.chat") },
              { value: "email", label: t("mediums.email") },
            ]}
            allLabel={t("mediums.all")}
          />
          <FilterMenuPill
            label={t("filters.category")}
            clearLabel={t("filters.clear", { name: t("filters.category") })}
            value={category}
            onChange={setCategory}
            options={[
              { value: "no_answer", label: t("categories.no_answer") },
              { value: "all", label: t("categories.all") },
            ]}
            allLabel={t("categories.conversations")}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-4 p-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <ErrorState compact onRetry={refetch} />
        ) : sessions.length === 0 ? (
          <EmptyState
            compact
            icon={filtering ? MagnifyingGlassIcon : ChatsCircleIcon}
            title={filtering ? t("list.noResults") : t("list.empty")}
          />
        ) : (
          <>
            {sessions.map((session, index) => (
              <Fragment key={session.id}>
                {(index === 0 || !sameDay(session, sessions[index - 1])) && (
                  <DayHeader date={lastActivityAt(session)} />
                )}
                <ConversationListItem
                  session={session}
                  selected={session.id === selectedId}
                  onSelect={onSelect}
                />
              </Fragment>
            ))}
            {hasMore && (
              <div className="p-3">
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={isFetching}
                  onClick={() => setLimit((current) => current + PAGE_SIZE)}
                >
                  {isFetching ? t("list.loading") : t("list.loadMore")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t px-3 py-2 text-xs text-muted-foreground">
        {t("list.count", { count: data?.total || 0 })}
      </div>
    </div>
  );
}

/**
 * Whether two rows fall on the same local calendar day — of `lastActivityAt`, the
 * date each row prints and the one the list is sorted by, so a day's rows are
 * always consecutive. "Load more" re-renders the whole grown list rather than
 * appending a page, so a day split across two pages still gets one header.
 */
const sameDay = (a: SessionListItem, b: SessionListItem) =>
  lastActivityAt(a).toDateString() === lastActivityAt(b).toDateString();

/**
 * The day a run of rows belongs to, the way a mail client separates them:
 * "Aujourd'hui", "Hier", then the weekday and date — with the year only once
 * it is not this one. Sticky, so the day of the row being read stays on screen.
 */
function DayHeader({ date }: { date: Date }) {
  const { t } = useTranslation("calls");
  const locale = useDateFnsLocale();
  const label = isToday(date)
    ? t("list.today")
    : isYesterday(date)
      ? t("list.yesterday")
      : format(
          date,
          isThisYear(date) ? t("list.dayFormat") : t("list.dayFormatOtherYear"),
          { locale },
        );

  return (
    <div className="sticky top-0 z-10 border-b bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
  );
}
